import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Plus, ExternalLink, Trash2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const GRAPHIC_STATUSES = [
  { value: "designing", label: "Designing", color: "#F59E0B", role: "editor" },
  { value: "editing", label: "Editing", color: "#3B82F6", role: "editor" },
  { value: "ready_for_review", label: "Ready for Review", color: "#8B5CF6", role: "editor" },
  { value: "approved", label: "Approved", color: "#10B981", role: "smm" },
  { value: "published", label: "Published", color: "#E4E4E7", role: "smm" },
  { value: "revision", label: "Revision", color: "#EF4444", role: "smm" },
];

const STATUS_MAP = GRAPHIC_STATUSES.reduce((a, s) => { a[s.value] = s; return a; }, {});

const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = {
  title: "",
  client_id: "",
  date: today(),
  design_link: "",
  published_link: "",
  status: "designing",
  assigned_editor_id: "",
  assigned_smm_id: "",
  notes: "",
};

export default function Graphics() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const role = user?.role;
  const canCreate = ["admin", "smm", "editor"].includes(role);
  const canDelete = ["admin", "smm"].includes(role);

  const load = async () => {
    const [g, u, c] = await Promise.all([
      api.get("/graphics"),
      api.get("/users"),
      api.get("/clients"),
    ]);
    setItems(g.data);
    setUsers(u.data);
    setClients(c.data);
  };
  useEffect(() => { load(); }, []);

  const editors = users.filter((u) => u.role === "editor" || u.role === "admin");
  const smms = users.filter((u) => u.role === "smm" || u.role === "admin");
  const findUser = (id) => users.find((u) => u.user_id === id);
  const findClient = (id) => clients.find((c) => c.client_id === id);

  const allowedStatusForMe = (currentStatus) => {
    if (role === "admin") return GRAPHIC_STATUSES.map((s) => s.value);
    if (role === "editor") {
      // Editor can set: designing, editing, ready_for_review
      return ["designing", "editing", "ready_for_review"];
    }
    if (role === "smm") {
      return ["approved", "published", "revision"];
    }
    return [];
  };

  const changeStatus = async (g, newStatus) => {
    try {
      const r = await api.patch(`/graphics/${g.graphic_id}`, { status: newStatus });
      setItems((p) => p.map((x) => (x.graphic_id === g.graphic_id ? r.data : x)));
      toast.success("Status updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const submit = async () => {
    if (!form.title) { toast.error("Title required"); return; }
    try {
      const payload = {
        ...form,
        client_id: form.client_id || null,
        assigned_editor_id: form.assigned_editor_id || null,
        assigned_smm_id: form.assigned_smm_id || null,
      };
      const r = await api.post("/graphics", payload);
      setItems((p) => [r.data, ...p]);
      setOpen(false);
      setForm(EMPTY);
      toast.success("Graphic created");
    } catch (e) {
      toast.error("Failed");
    }
  };

  const remove = async (g) => {
    if (!window.confirm(`Delete "${g.title}"?`)) return;
    try {
      await api.delete(`/graphics/${g.graphic_id}`);
      setItems((p) => p.filter((x) => x.graphic_id !== g.graphic_id));
      toast.success("Deleted");
    } catch (e) {
      toast.error("Failed");
    }
  };

  return (
    <div data-testid="graphics-page">
      <PageHeader
        eyebrow="Design Pipeline"
        title="Graphics"
        description="Editors design → SMM approves & publishes. Revisions bounce back to editor."
        actions={
          canCreate && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-graphic-btn" className="rounded-none bg-[#00C97A] hover:bg-[#00B26B] text-black">
                  <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  <span className="uppercase tracking-[0.15em] text-xs">New Graphic</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-neutral-950 border border-neutral-800 rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl">New Graphic</DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                  <div className="md:col-span-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Title</Label>
                    <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-2 rounded-none bg-neutral-900 border-neutral-800" data-testid="graphic-title" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Date</Label>
                    <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-2 rounded-none bg-neutral-900 border-neutral-800" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Client</Label>
                    <Select value={form.client_id || "none"} onValueChange={(v) => setForm({ ...form, client_id: v === "none" ? "" : v })}>
                      <SelectTrigger className="mt-2 rounded-none bg-neutral-900 border-neutral-800"><SelectValue placeholder="Select client" /></SelectTrigger>
                      <SelectContent className="rounded-none bg-neutral-950 border-neutral-800">
                        <SelectItem value="none">—</SelectItem>
                        {clients.map((c) => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Design Link</Label>
                    <Input value={form.design_link} placeholder="Figma / Drive link" onChange={(e) => setForm({ ...form, design_link: e.target.value })} className="mt-2 rounded-none bg-neutral-900 border-neutral-800" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Editor</Label>
                    <Select value={form.assigned_editor_id || "none"} onValueChange={(v) => setForm({ ...form, assigned_editor_id: v === "none" ? "" : v })}>
                      <SelectTrigger className="mt-2 rounded-none bg-neutral-900 border-neutral-800"><SelectValue placeholder="Assign" /></SelectTrigger>
                      <SelectContent className="rounded-none bg-neutral-950 border-neutral-800">
                        <SelectItem value="none">—</SelectItem>
                        {editors.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">SMM</Label>
                    <Select value={form.assigned_smm_id || "none"} onValueChange={(v) => setForm({ ...form, assigned_smm_id: v === "none" ? "" : v })}>
                      <SelectTrigger className="mt-2 rounded-none bg-neutral-900 border-neutral-800"><SelectValue placeholder="Assign" /></SelectTrigger>
                      <SelectContent className="rounded-none bg-neutral-950 border-neutral-800">
                        <SelectItem value="none">—</SelectItem>
                        {smms.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Notes</Label>
                    <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-2 rounded-none bg-neutral-900 border-neutral-800" />
                  </div>
                </div>
                <DialogFooter className="mt-4">
                  <Button onClick={submit} data-testid="graphic-submit-btn" className="rounded-none bg-[#00C97A] hover:bg-[#00B26B] text-black">Create</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )
        }
      />

      <div className="px-6 lg:px-10 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-px bg-neutral-900 border border-neutral-900">
          {items.length === 0 && (
            <div className="bg-neutral-950 px-6 py-12 text-center text-neutral-500 col-span-full">
              No graphics yet. Hit <span className="text-[#00C97A]">New Graphic</span> to start designing ✏️
            </div>
          )}
          {items.map((g) => {
            const s = STATUS_MAP[g.status];
            const cli = findClient(g.client_id);
            const editor = findUser(g.assigned_editor_id);
            const smm = findUser(g.assigned_smm_id);
            const allowed = allowedStatusForMe(g.status);
            return (
              <div key={g.graphic_id} className="bg-neutral-950 p-5 hover:bg-neutral-900/40 transition-colors" data-testid={`graphic-card-${g.graphic_id}`} style={{ borderTop: `2px solid ${s?.color || "#262626"}` }}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] mb-1.5" style={{ color: s?.color }}>
                      {s?.label || g.status}
                    </div>
                    <div className="font-heading text-lg font-semibold leading-tight">{g.title}</div>
                    {cli && <div className="text-xs text-[#00C97A] uppercase tracking-[0.15em] mt-1">{cli.name}</div>}
                  </div>
                  {canDelete && (
                    <button onClick={() => remove(g)} className="text-neutral-600 hover:text-red-400 transition-colors" data-testid={`delete-graphic-${g.graphic_id}`}>
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3 mt-4 text-xs">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Date</div>
                    <div className="font-mono text-neutral-300 mt-0.5">{g.date || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Team</div>
                    <div className="text-neutral-300 mt-0.5 truncate">
                      {editor?.name || "—"} → {smm?.name || "—"}
                    </div>
                  </div>
                </div>
                <div className="flex gap-3 mt-3">
                  {g.design_link && <a href={g.design_link} target="_blank" rel="noreferrer" className="text-xs text-neutral-400 hover:text-[#00C97A] inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Design</a>}
                  {g.published_link && <a href={g.published_link} target="_blank" rel="noreferrer" className="text-xs text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Live</a>}
                </div>
                {allowed.length > 0 && (
                  <div className="mt-4 pt-3 border-t border-neutral-900">
                    <div className="text-[9px] uppercase tracking-[0.2em] text-neutral-600 mb-2">
                      {role === "editor" ? "Editor controls" : role === "smm" ? "SMM controls" : "Admin"}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {GRAPHIC_STATUSES.map((opt) => {
                        const can = allowed.includes(opt.value);
                        const active = g.status === opt.value;
                        return (
                          <button
                            key={opt.value}
                            disabled={!can}
                            onClick={() => changeStatus(g, opt.value)}
                            data-testid={`graphic-status-${g.graphic_id}-${opt.value}`}
                            className={`px-2 py-1 text-[10px] uppercase tracking-[0.15em] rounded-full border transition-colors disabled:opacity-30 disabled:cursor-not-allowed`}
                            style={
                              active
                                ? { borderColor: opt.color, color: opt.color, backgroundColor: "#171717" }
                                : { borderColor: "#262626", color: "#737373" }
                            }
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
